/**
 * Binder rewards — the collection-milestone half of the Pal Stars free pack
 * economy. Every reward here is verified server-side from the member's own
 * banked cards and binder pockets, then credited automatically.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { FaqSection, faqSchema, type FaqItem } from "@/components/PageFaq";
import { breadcrumbSchema } from "@/lib/schema";
import { getMyCards } from "@/lib/binder.functions";
import { completeChallenge, getChallengeState } from "@/lib/challenges.functions";
import { CHALLENGE_BY_ID } from "@/lib/challenges-shared";
import {
  BINDER_CAPACITY,
  BINDER_SECTIONS,
  BINDER_SLOTS_PER_PAGE,
  CHASE_RARITIES,
} from "@/lib/binder-shared";
import { useSession } from "@/lib/user-data";

/** Rewards on this page, in claim order. All are verified from the database. */
const BINDER_REWARD_IDS = [
  "file-nine",
  "full-color-page",
  "gem-mint",
  "chase-card",
  "open-fifty",
  "binder-complete",
] as const;

const FAQ: FaqItem[] = [
  {
    q: "What are Pal Stars binder rewards?",
    a: `Binder rewards are free Palworld booster packs paid for collection milestones: filling your first nine-pocket page, completing a color page, pulling a Gem Mint 10, landing an SSP, OSR or SSS chase card, opening 50 packs, and filling all ${BINDER_CAPACITY} pockets. Each one pays 1, 5 or 10 packs automatically.`,
  },
  {
    q: "How are binder rewards verified?",
    a: "Entirely on the server, from your own banked cards and binder pockets. There is nothing to upload and no screenshot to send — press Claim and the server checks the milestone against your collection before crediting packs.",
  },
  {
    q: "Can I claim a binder reward more than once?",
    a: "No. Each milestone pays out once per account. A unique claim row is written when it credits, so a second attempt returns an \"already earned\" message instead of more packs.",
  },
  {
    q: "How many packs can binder rewards pay in total?",
    a: `The six binder milestones pay ${BINDER_REWARD_IDS.reduce((n, id) => n + (CHALLENGE_BY_ID[id]?.packs ?? 0), 0)} bonus booster packs in total, on top of the 12 free daily packs and your daily Palsino spins.`,
  },
  {
    q: "Where do the packs go once a reward credits?",
    a: "Straight into your bonus pack balance. Open them on the pack opening page and file the cards into the color section they belong to — chase pulls go to the gold chase pages at the back of the binder.",
  },
];

export const Route = createFileRoute("/binder-rewards")({
  head: () => ({
    meta: [
      { title: "Binder Rewards — Free Palworld Booster Packs | Pal Stars" },
      {
        name: "description",
        content:
          "Track your Pal Stars binder milestones and claim free Palworld booster packs for filling pages, pulling Gem Mint 10s and landing SSP, OSR or SSS chase cards.",
      },
      { property: "og:title", content: "Binder Rewards — Pal Stars" },
      {
        property: "og:description",
        content: "Claim free Palworld booster packs for binder milestones — verified automatically from your collection.",
      },
      { property: "og:url", content: "https://palstars.com/binder-rewards" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://palstars.com/binder-rewards" }],
    scripts: [
      breadcrumbSchema([["Home", "/"], ["Binder Rewards", "/binder-rewards"]]),
      faqSchema(FAQ, "https://palstars.com/binder-rewards"),
    ],
  }),
  component: BinderRewardsPage,
});

type ClaimRow = { challenge_id: string; packs_awarded: number; created_at: string };

interface Progress {
  filled: number;
  sections: { id: string; label: string; tint: string; filled: number; total: number }[];
  chase: number;
  gemMint: number;
  cards: number;
}

function BinderRewardsPage() {
  const { session, loading } = useSession();
  const cardsFn = useServerFn(getMyCards);
  const stateFn = useServerFn(getChallengeState);
  const claimFn = useServerFn(completeChallenge);

  const [claims, setClaims] = useState<ClaimRow[]>([]);
  const [bonus, setBonus] = useState(0);
  const [progress, setProgress] = useState<Progress | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [messages, setMessages] = useState<Record<string, { ok: boolean; text: string }>>({});

  const claimed = useMemo(() => new Set(claims.map((c) => c.challenge_id)), [claims]);

  const refresh = useCallback(async () => {
    if (!session) return;
    const [state, cards] = await Promise.all([
      stateFn({}) as Promise<{ claimed: ClaimRow[]; bonusPacks: number }>,
      cardsFn({}),
    ]);
    setClaims(state.claimed ?? []);
    setBonus(state.bonusPacks);

    const inBinder = (cards ?? []).filter((c) => c.status === "binder" && c.binder_slot !== null);
    const sections = BINDER_SECTIONS.map((s) => ({
      id: s.id,
      label: s.label,
      tint: s.tint,
      total: s.pages.length * BINDER_SLOTS_PER_PAGE,
      filled: inBinder.filter((c) => s.pages.includes(Math.floor((c.binder_slot ?? 0) / BINDER_SLOTS_PER_PAGE))).length,
    }));
    setProgress({
      filled: inBinder.length,
      sections,
      chase: (cards ?? []).filter((c) => CHASE_RARITIES.includes(c.rarity ?? "")).length,
      gemMint: (cards ?? []).filter((c) => Number(c.grade) >= 10).length,
      cards: (cards ?? []).length,
    });
  }, [session, stateFn, cardsFn]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function claim(id: string) {
    setBusy(id);
    try {
      const res = (await claimFn({ data: { challenge_id: id, answer: "" } })) as { ok?: boolean; message?: string };
      setMessages((m) => ({ ...m, [id]: { ok: Boolean(res.ok), text: res.message ?? "Checked." } }));
      await refresh();
    } catch {
      setMessages((m) => ({ ...m, [id]: { ok: false, text: "That check didn't go through — try again." } }));
    } finally {
      setBusy(null);
    }
  }

  const totalPacks = BINDER_REWARD_IDS.reduce((n, id) => n + (CHALLENGE_BY_ID[id]?.packs ?? 0), 0);
  const earned = BINDER_REWARD_IDS.filter((id) => claimed.has(id)).reduce(
    (n, id) => n + (CHALLENGE_BY_ID[id]?.packs ?? 0),
    0,
  );

  return (
    <div className="mx-auto max-w-5xl space-y-10 px-4 py-10 sm:px-6">
      <header>
        <p className="text-xs uppercase tracking-widest text-primary">Pal Stars binder</p>
        <h1 className="mt-2 text-3xl font-bold">Binder rewards</h1>
        <p className="aeo-answer mt-3 max-w-3xl text-sm text-muted-foreground">
          Binder rewards pay free Palworld booster packs for collection milestones — filling your first nine-pocket page,
          completing a color page, pulling a Gem Mint 10, landing an SSP, OSR or SSS chase card, opening 50 packs and
          filling all {BINDER_CAPACITY} pockets. Every milestone is verified from your own collection on the server and
          credited automatically, {totalPacks} bonus packs in total.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Link to="/binder" className="rounded-md border px-5 py-2.5 text-sm font-semibold hover:bg-card">
            Open your binder
          </Link>
          <Link
            to="/pack-opening"
            className="rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:brightness-110"
          >
            Open your packs →
          </Link>
          <Link to="/rewards" className="rounded-md border px-5 py-2.5 text-sm font-semibold hover:bg-card">
            All free pack challenges
          </Link>
        </div>
      </header>

      {loading ? (
        <p className="text-sm text-muted-foreground">Checking your account…</p>
      ) : !session ? (
        <section className="glass rounded-2xl border p-5">
          <h2 className="text-xl font-bold">Create a free account to collect</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Binder rewards read your own cards, so they need a free Pal Stars account. Signing up also grants 5 welcome
            packs and 20 Palsino spins.
          </p>
          <Link
            to="/auth"
            className="mt-4 inline-block rounded-lg border border-primary/50 bg-primary/10 px-5 py-2 text-sm font-bold text-primary hover:bg-primary/20"
          >
            Create a free account
          </Link>
        </section>
      ) : (
        <>
          <section aria-labelledby="binder-progress" className="glass rounded-2xl border p-5 sm:p-6">
            <h2 id="binder-progress" className="text-xl font-bold">
              Your binder progress
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {progress
                ? `${progress.filled} of ${BINDER_CAPACITY} pockets filled · ${progress.cards} cards banked · ${progress.chase} chase card${progress.chase === 1 ? "" : "s"} · ${progress.gemMint} Gem Mint 10${progress.gemMint === 1 ? "" : "s"} · ${bonus} bonus pack${bonus === 1 ? "" : "s"} ready`
                : "Loading your collection…"}
            </p>
            <ul className="mt-4 grid gap-3 sm:grid-cols-2">
              {(progress?.sections ?? []).map((s) => (
                <li key={s.id} className="rounded-xl border p-3" style={{ borderColor: `${s.tint}55` }}>
                  <div className="flex items-center justify-between text-sm font-semibold">
                    <span style={{ color: s.tint }}>{s.label}</span>
                    <span className="tabular-nums text-muted-foreground">
                      {s.filled}/{s.total}
                    </span>
                  </div>
                  <div className="mt-2 h-2 overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${Math.round((s.filled / s.total) * 100)}%`, background: s.tint }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          </section>

          <section aria-labelledby="binder-milestones">
            <h2 id="binder-milestones" className="text-xl font-bold">
              Milestone rewards — {earned}/{totalPacks} packs earned
            </h2>
            <ul className="mt-4 space-y-3">
              {BINDER_REWARD_IDS.map((id) => {
                const def = CHALLENGE_BY_ID[id];
                if (!def) return null;
                const done = claimed.has(id);
                const msg = messages[id];
                return (
                  <li key={id} className="glass flex flex-wrap items-center justify-between gap-3 rounded-xl border p-4">
                    <div className="min-w-[16rem] flex-1">
                      <p className="font-semibold">
                        {def.title} <span className="text-primary">+{def.packs} pack{def.packs === 1 ? "" : "s"}</span>
                      </p>
                      <p className="mt-1 text-sm text-muted-foreground">{def.detail}</p>
                      {msg && (
                        <p className={`mt-1 text-sm ${msg.ok ? "text-green-400" : "text-amber-400"}`} aria-live="polite">
                          {msg.text}
                        </p>
                      )}
                    </div>
                    {done ? (
                      <span className="rounded-full bg-green-500/15 px-4 py-2 text-sm font-semibold text-green-400">
                        Claimed
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => void claim(id)}
                        disabled={busy === id}
                        className="rounded-full bg-primary px-5 py-2 text-sm font-bold text-primary-foreground hover:brightness-110 disabled:opacity-50"
                      >
                        {busy === id ? "Checking…" : "Claim"}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        </>
      )}

      <FaqSection items={FAQ} heading="Binder rewards FAQ" />
    </div>
  );
}
